import { FontAwesome5 } from '@expo/vector-icons';
import { styled, ThemeType } from '@openeducation/oe-rn-themes';
import React, {
  memo,
  RefAttributes,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { StyleProp, ViewStyle, StyleSheet } from 'react-native';
import RNSelectDropdown, {
  SelectDropdownProps,
} from 'react-native-select-dropdown';

import type { DropdownItemType } from './select-dropdown-input.types';
import { OEText } from '../../atoms/oe-text/oe-text';
import SelectDropdownItem from '../../atoms/select-dropdown-item/select-dropdown-item';
import { isWebPlatform } from '../../utils';

export interface SelectDropdownInputBaseProps
  extends Omit<
    SelectDropdownProps,
    'buttonTextAfterSelection' | 'rowTextForSelection'
  > {
  buttonTextAfterSelection?: (selectedItem: any, index?: number) => string;
  rowTextForSelection?: (item: any, index: number) => string;
  renderCustomRowChild?: (item: DropdownItemType, index?: number) => ReactNode;
  renderCustomSelectedText?: (item: DropdownItemType) => ReactNode;
  arrowColor?: string;
  width?: number | string;
  style?: StyleProp<ViewStyle>[] | StyleProp<ViewStyle>;
}

export const SelectDropdownInputBase = React.forwardRef<
  RNSelectDropdown,
  SelectDropdownInputBaseProps
>((inputProps, ref) => {
  // NOTE: style prop is passed from styled component as an array
  // and this format is not supported by react-native-select-dropdown. It needs to be excluded.
  const {
    renderCustomRowChild,
    renderCustomSelectedText,
    style: _,
    ...dropdownProps
  } = inputProps;
  const [focused, setFocused] = useState<boolean>(false);

  useEffect(() => {
    const body = isWebPlatform ? document.querySelector('body') : null;
    const initialOverflow =
      (isWebPlatform && body && body.style.overflow) || 'auto';

    if (isWebPlatform && body) {
      // NOTE: block body scroll when the dropdown is open on web
      if (focused) {
        body.style.overflow = 'hidden';
      } else {
        body.style.overflow = initialOverflow;
      }
    }

    return () => {
      if (isWebPlatform && body) {
        body.style.overflow = initialOverflow;
      }
    };
  }, [focused]);

  const renderDropdownIcon = () => (
    <IconArrow color={inputProps.arrowColor} name="sort-down" />
  );
  const renderSelectedButtonText = (selectedItem: DropdownItemType): any => {
    return (
      <OEText fontSize={24} fontType="bold" numberOfLines={1}>
        {selectedItem.value}
      </OEText>
    );
  };
  const renderRowChild = (item: DropdownItemType) => {
    if (renderCustomRowChild) {
      return (
        <SelectDropdownItem indicator={item.indicator}>
          {renderCustomRowChild(item)}
        </SelectDropdownItem>
      );
    }
    return (
      <SelectDropdownItem indicator={item.indicator}>
        {item.value}
      </SelectDropdownItem>
    );
  };

  return (
    <DropdownBase
      ref={ref}
      rowTextForSelection={(item) => item} // NOTE: this prop is required in types 🤔
      dropdownIconPosition="right"
      buttonTextAfterSelection={renderSelectedButtonText}
      renderDropdownIcon={renderDropdownIcon}
      renderCustomizedRowChild={renderRowChild}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      {...dropdownProps}
    />
  );
});

type DropdownType = {
  isSelected?: boolean;
  theme: ThemeType;
  width?: number | string;
} & SelectDropdownProps &
  RefAttributes<RNSelectDropdown>;

const DropdownBase = styled(RNSelectDropdown).attrs<DropdownType>(
  ({
    theme,
    isSelected,
    width,
    buttonStyle,
    buttonTextStyle,
    dropdownStyle,
    rowStyle,
    rowTextStyle,
  }: DropdownType) => ({
    buttonStyle: StyleSheet.flatten([
      {
        height: 56,
        backgroundColor: theme.color.white,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.color.black,
        width: width || 502,
        flexDirection: 'row-reverse',
        alignItems: 'center',
      },
      buttonStyle,
    ]) as StyleProp<ViewStyle>,
    buttonTextStyle: StyleSheet.flatten([
      {
        fontFamily: isSelected
          ? theme.font.bold?.fontFamily
          : theme.font.italic?.fontFamily,
        textAlign: 'left',
        fontSize: 24,
        flex: 1,
      },
      buttonTextStyle,
    ]) as StyleProp<ViewStyle>,
    dropdownStyle: StyleSheet.flatten([
      {
        backgroundColor: theme.color.white,
        paddingTop: 10,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        flexGrow: 1,
      },
      dropdownStyle,
    ]) as StyleProp<ViewStyle>,
    rowStyle: StyleSheet.flatten([
      {
        paddingVertical: 8,
        marginHorizontal: 24,
        borderBottomWidth: 1,
        borderBottomColor: theme.color.light1,
      },
      rowStyle,
    ]) as StyleProp<ViewStyle>,
    rowTextStyle: StyleSheet.flatten([
      {
        fontFamily: theme.font.regular.fontFamily,
        color: theme.color.black,
        fontSize: 16,
        lineHeight: 24,
      },
      rowTextStyle,
    ]) as StyleProp<ViewStyle>,
  })
)``;

const IconArrow = styled(FontAwesome5).attrs(({ theme, color }) => ({
  color: color || theme.color.dark1,
  size: 24,
}))`
  margin-right: 10px;
  margin-bottom: 10px;
`;

export default memo(SelectDropdownInputBase);
